import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import TagPicker from '../../components/common/TagPicker';
import {
  EventDefinition,
  EventCategory,
  Workflow,
  Tag,
} from '../../types';
import { CreateEventDefinitionRequest } from '../../services/eventDefinitionService';
import {
  EVENT_CATEGORY_OPTIONS,
  SCHEDULE_TYPE_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  INTERNAL_SOURCE_OPTIONS,
  WORKFLOW_LIFECYCLE_OPTIONS,
} from './triggerOptions';

// Shared input className matching <Select> so the per-category sub-form
// fields look identical regardless of input type.
const fieldInputClass =
  'w-full h-9 px-3 bg-surface border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none transition-colors duration-fast';

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

/** Wrapper that gives a raw input the same label styling as <Select>. */
const Field: React.FC<FieldProps> = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-neutral-500 mb-1.5">
      {label}
      {required && <span className="text-danger-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

// =============================================================================
// Types
// =============================================================================

interface EventDefinitionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEventDefinitionRequest, id?: string) => Promise<void>;
  editing: EventDefinition | null;
  workflows: Workflow[];
  tags: Tag[];
  /** Refresh tags after one is created on the fly via TagPicker. */
  onTagsChanged: () => void;
}

/**
 * Per-category config shape kept loose because each category has its own
 * fields. Stored as the JSONB `config` blob on the backend.
 */
interface FormState {
  // Information
  name: string;
  description: string;
  tagIds: string[];

  // Type
  category: EventCategory;

  // Details — schedule
  scheduleType: string;
  dayOfWeek: string;
  dayOfMonth: string;
  time: string;
  cronExpression: string;
  oneTimeDate: string;

  // Details — external
  externalSource: string;

  // Details — internal
  internalSourceKind: string;
  internalDescriptor: string;

  // Details — workflow
  workflowSourceId: string;
  workflowLifecycle: string;
}

const BLANK: FormState = {
  name: '',
  description: '',
  tagIds: [],
  category: 'schedule',

  scheduleType: 'weekly',
  dayOfWeek: '5',
  dayOfMonth: '',
  time: '12:00',
  cronExpression: '',
  oneTimeDate: '',

  externalSource: '',

  internalSourceKind: 'portfolio',
  internalDescriptor: '',

  workflowSourceId: '',
  workflowLifecycle: 'completed',
};

type StepKey = 'information' | 'type' | 'details';

const STEPS: { key: StepKey; num: number; label: string }[] = [
  { key: 'information', num: 1, label: 'Information' },
  { key: 'type', num: 2, label: 'Type' },
  { key: 'details', num: 3, label: 'Details' },
];

// =============================================================================
// Modal
// =============================================================================

const EventDefinitionFormModal: React.FC<EventDefinitionFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editing,
  workflows,
  tags,
  onTagsChanged,
}) => {
  const [form, setForm] = useState<FormState>(BLANK);
  const [step, setStep] = useState<StepKey>('information');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate from `editing` whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      const cfg = (editing.config || {}) as Record<string, unknown>;
      setForm({
        name: editing.name,
        description: editing.description ?? '',
        tagIds: editing.tags.map(t => t.id),
        category: editing.category,
        scheduleType: (cfg.scheduleType as string) ?? 'weekly',
        dayOfWeek: (cfg.dayOfWeek as string) ?? '5',
        dayOfMonth: (cfg.dayOfMonth as string) ?? '',
        time: (cfg.time as string) ?? '12:00',
        cronExpression: (cfg.cronExpression as string) ?? '',
        oneTimeDate: (cfg.oneTimeDate as string) ?? '',
        externalSource: (cfg.externalSource as string) ?? '',
        internalSourceKind: (cfg.internalSourceKind as string) ?? 'portfolio',
        internalDescriptor: (cfg.internalDescriptor as string) ?? '',
        workflowSourceId: (cfg.workflowSourceId as string) ?? '',
        workflowLifecycle: (cfg.workflowLifecycle as string) ?? 'completed',
      });
    } else {
      setForm(BLANK);
    }
    setStep('information');
    setError(null);
  }, [isOpen, editing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Build the JSONB config payload from the relevant per-category fields
  const buildConfig = (): Record<string, unknown> => {
    switch (form.category) {
      case 'schedule':
        return {
          scheduleType: form.scheduleType,
          ...(form.scheduleType === 'weekly' && { dayOfWeek: form.dayOfWeek, time: form.time }),
          ...(form.scheduleType === 'monthly' && { dayOfMonth: form.dayOfMonth, time: form.time }),
          ...(form.scheduleType === 'daily' && { time: form.time }),
          ...(form.scheduleType === 'cron' && { cronExpression: form.cronExpression }),
          ...(form.scheduleType === 'one_time' && { oneTimeDate: form.oneTimeDate }),
        };
      case 'external':
        return { externalSource: form.externalSource };
      case 'internal':
        return {
          internalSourceKind: form.internalSourceKind,
          internalDescriptor: form.internalDescriptor,
        };
      case 'workflow':
        return {
          workflowSourceId: form.workflowSourceId,
          workflowLifecycle: form.workflowLifecycle,
        };
      default:
        return {};
    }
  };

  // ─── Step gating ─────────────────────────────────────────────────────────
  const canProceed = (): boolean => {
    switch (step) {
      case 'information':
        return form.name.trim().length > 0;
      case 'type':
        return true;
      case 'details':
        if (form.category === 'schedule') {
          if (form.scheduleType === 'weekly') return !!form.dayOfWeek;
          if (form.scheduleType === 'monthly') return !!form.dayOfMonth;
          if (form.scheduleType === 'cron') return form.cronExpression.trim().length > 0;
          if (form.scheduleType === 'one_time') return !!form.oneTimeDate;
          return true;
        }
        if (form.category === 'external') return form.externalSource.trim().length > 0;
        if (form.category === 'workflow') return !!form.workflowSourceId;
        return true;
    }
  };

  const goNext = () => {
    const idx = STEPS.findIndex(s => s.key === step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
  };
  const goBack = () => {
    const idx = STEPS.findIndex(s => s.key === step);
    if (idx > 0) setStep(STEPS[idx - 1].key);
  };

  const isLastStep = step === 'details';
  const isFirstStep = step === 'information';
  const currentStepNum = STEPS.find(s => s.key === step)?.num ?? 1;

  // ─── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError(null);

    const payload: CreateEventDefinitionRequest = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      category: form.category,
      tagIds: form.tagIds,
      config: buildConfig(),
    };

    try {
      setSubmitting(true);
      await onSubmit(payload, editing?.id);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save event definition.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 animate-fade-in" onClick={onClose} />

      <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-3xl w-full mx-auto z-10 animate-scale-in flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-neutral-900">
              {editing ? 'Edit Event Definition' : 'New Event Definition'}
            </h2>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress steps */}
          <div className="flex items-center mt-4 space-x-2">
            {STEPS.map((s, i) => {
              const isCompleted = s.num < currentStepNum;
              const isCurrent = s.key === step;
              return (
                <React.Fragment key={s.key}>
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      isCompleted || isCurrent
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-200 text-neutral-600'
                    }`}
                  >
                    {s.num}
                  </div>
                  <span
                    className={`text-sm ${
                      isCompleted || isCurrent ? 'text-primary-600 font-medium' : 'text-neutral-500'
                    }`}
                  >
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 ${isCompleted ? 'bg-primary-600' : 'bg-neutral-200'}`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content — scrolls only when content overflows the modal max-height */}
        <div className="px-6 py-5 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
              {error}
            </div>
          )}

          {/* ── Step 1 · Information ───────────────────────────── */}
          {step === 'information' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Information</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Name the event and tag it for grouping.
                </p>
              </div>

              <Field label="Name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => update('name', e.target.value)}
                  placeholder="e.g. Quarterly Earnings Release"
                  className={fieldInputClass}
                />
              </Field>
              <Field label="Description">
                <input
                  type="text"
                  value={form.description}
                  onChange={e => update('description', e.target.value)}
                  placeholder="What this event listens for"
                  className={fieldInputClass}
                />
              </Field>
              <TagPicker
                label="Tags"
                value={form.tagIds}
                onChange={ids => update('tagIds', ids)}
                tags={tags}
                onTagsChanged={onTagsChanged}
                placeholder="Add tag — type to search or create"
              />
            </div>
          )}

          {/* ── Step 2 · Type ──────────────────────────────────── */}
          {step === 'type' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Type</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Pick the category that best fits what triggers this event.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {EVENT_CATEGORY_OPTIONS.map(opt => {
                  const active = form.category === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update('category', opt.value)}
                      className={`text-left px-3 py-3 rounded-lg border transition-colors ${
                        active
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-300 hover:border-neutral-400 bg-surface text-neutral-700'
                      }`}
                    >
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-[11px] text-neutral-500 mt-0.5 leading-tight">
                        {opt.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 3 · Details ───────────────────────────────── */}
          {step === 'details' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Details</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Configure how the {form.category} event fires.
                </p>
              </div>

              {form.category === 'schedule' && (
                <div className="space-y-3">
                  <Select
                    label="Schedule type"
                    value={form.scheduleType}
                    onChange={e => update('scheduleType', e.target.value)}
                    options={SCHEDULE_TYPE_OPTIONS}
                  />
                  {form.scheduleType === 'weekly' && (
                    <div className="grid grid-cols-2 gap-3">
                      <Select
                        label="Day of week"
                        value={form.dayOfWeek}
                        onChange={e => update('dayOfWeek', e.target.value)}
                        options={DAY_OF_WEEK_OPTIONS}
                      />
                      <Field label="Time">
                        <input
                          type="time"
                          value={form.time}
                          onChange={e => update('time', e.target.value)}
                          className={fieldInputClass}
                        />
                      </Field>
                    </div>
                  )}
                  {form.scheduleType === 'monthly' && (
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Day of month">
                        <input
                          type="number"
                          min={1}
                          max={31}
                          value={form.dayOfMonth}
                          onChange={e => update('dayOfMonth', e.target.value)}
                          placeholder="1–31"
                          className={fieldInputClass}
                        />
                      </Field>
                      <Field label="Time">
                        <input
                          type="time"
                          value={form.time}
                          onChange={e => update('time', e.target.value)}
                          className={fieldInputClass}
                        />
                      </Field>
                    </div>
                  )}
                  {form.scheduleType === 'daily' && (
                    <Field label="Time">
                      <input
                        type="time"
                        value={form.time}
                        onChange={e => update('time', e.target.value)}
                        className={fieldInputClass}
                      />
                    </Field>
                  )}
                  {form.scheduleType === 'cron' && (
                    <Field label="Cron expression">
                      <input
                        type="text"
                        value={form.cronExpression}
                        onChange={e => update('cronExpression', e.target.value)}
                        placeholder="e.g. 0 12 * * 5"
                        className={fieldInputClass}
                      />
                    </Field>
                  )}
                  {form.scheduleType === 'one_time' && (
                    <Field label="Date">
                      <input
                        type="date"
                        value={form.oneTimeDate}
                        onChange={e => update('oneTimeDate', e.target.value)}
                        className={fieldInputClass}
                      />
                    </Field>
                  )}
                </div>
              )}

              {form.category === 'external' && (
                <div className="space-y-3">
                  <Field label="Source" required>
                    <input
                      type="text"
                      value={form.externalSource}
                      onChange={e => update('externalSource', e.target.value)}
                      placeholder="e.g. FDA RSS feed, Bloomberg news, Webhook"
                      className={fieldInputClass}
                    />
                  </Field>
                  <p className="text-xs text-neutral-500">
                    Free text for now — wiring this to a real provider comes later.
                  </p>
                </div>
              )}

              {form.category === 'internal' && (
                <div className="space-y-3">
                  <Select
                    label="Source kind"
                    value={form.internalSourceKind}
                    onChange={e => update('internalSourceKind', e.target.value)}
                    options={INTERNAL_SOURCE_OPTIONS}
                  />
                  <Field label="Descriptor">
                    <input
                      type="text"
                      value={form.internalDescriptor}
                      onChange={e => update('internalDescriptor', e.target.value)}
                      placeholder="e.g. drawdown > 5%, new note tagged 'urgent'"
                      className={fieldInputClass}
                    />
                  </Field>
                </div>
              )}

              {form.category === 'workflow' && (
                <div className="space-y-3">
                  <Select
                    label="Source workflow"
                    required
                    value={form.workflowSourceId}
                    onChange={e => update('workflowSourceId', e.target.value)}
                    options={workflows.map(w => ({ value: w.id, label: w.name }))}
                    placeholder="Pick a workflow"
                  />
                  <Select
                    label="Lifecycle"
                    value={form.workflowLifecycle}
                    onChange={e => update('workflowLifecycle', e.target.value)}
                    options={WORKFLOW_LIFECYCLE_OPTIONS}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-between items-center shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={isFirstStep ? onClose : goBack}
            disabled={submitting}
          >
            {isFirstStep ? 'Cancel' : 'Back'}
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={isLastStep ? handleSubmit : goNext}
            disabled={!canProceed() || submitting}
          >
            {submitting
              ? 'Saving…'
              : isLastStep
                ? editing
                  ? 'Save Changes'
                  : 'Create Event'
                : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventDefinitionFormModal;
